import type { WmtsTile } from "./PVSTileService/TileGetters/WMTSTileGetter/WMTSTile";

class LinkedListNode<T> {
  prev: LinkedListNode<T> | null = null;
  next: LinkedListNode<T> | null = null;
  value: T;

  constructor(
    readonly key: string,
    value: T,
  ) {
    this.value = value;
  }
}

export class LRUCache<T> {
  map = new Map<string, LinkedListNode<T>>();
  head: LinkedListNode<T> | null = null;
  tail: LinkedListNode<T> | null = null;
  memoryFootprint = 0;

  constructor(
    readonly sizeOf: (value: T) => number,
    readonly capacity: number,
  ) {}

  get(key: string): T | undefined {
    const node = this.map.get(key);

    if (!node) {
      return undefined;
    }

    // Move to head (most recently used)
    if (node !== this.head) {
      this.moveToHead(node);
    }

    return node.value;
  }

  put(key: string, value: T): void {
    const existingNode = this.map.get(key);
    const newSize = this.sizeOf(value);

    if (existingNode) {
      const oldSize = this.sizeOf(existingNode.value);
      existingNode.value = value;

      this.memoryFootprint = this.memoryFootprint - oldSize + newSize;

      if (existingNode !== this.head) {
        this.moveToHead(existingNode);
      }
    } else {
      const newNode = new LinkedListNode(key, value);
      this.map.set(key, newNode);
      this.addToHead(newNode);
      this.memoryFootprint += newSize;
    }

    // Evict if necessary
    this.evict();
  }

  del(key: string): void {
    const node = this.map.get(key);
    if (!node) {
      return;
    }
    this.removeNode(node);
  }

  private addToHead(node: LinkedListNode<T>) {
    if (!this.head) {
      this.head = node;
      this.tail = node;
    } else {
      node.next = this.head;
      this.head.prev = node;
      this.head = node;
    }
  }

  private moveToHead(node: LinkedListNode<T>) {
    this.removeFromList(node);
    this.addToHead(node);
  }

  private removeFromList(node: LinkedListNode<T>) {
    if (node === this.head) {
      this.head = node.next;
    }

    if (node === this.tail) {
      this.tail = node.prev;
    }

    if (node.prev) {
      node.prev.next = node.next;
    }

    if (node.next) {
      node.next.prev = node.prev;
    }

    node.prev = null;
    node.next = null;
  }

  private removeNode(node: LinkedListNode<T>) {
    this.removeFromList(node);

    this.memoryFootprint -= this.sizeOf(node.value);

    this.map.delete(node.key);

    const tile: WmtsTile = node.value as WmtsTile;

    tile.unload();
  }

  private evict() {
    while (this.memoryFootprint > this.capacity && this.tail) {
      const nodeToEvict = this.tail;
      this.removeNode(nodeToEvict);
    }
  }
}
